import { FC, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { useQuery } from 'react-query';
import { getUnreadNotificationsCount } from '../../apis/backend';
import { colors, fontStyles } from '../../styles';
import { PushNotificationService } from '../../services/pushNotificationService';
import { reportError } from '../../utils/reporting';

interface Props {
  children: React.ReactNode;
}

const NotificationBadge: FC<Props> = ({ children }) => {
  const [fcmToken, setFcmToken] = useState<string | null>(null);

  useEffect(() => {
    // Only try to get token if there are enabled notification preferences
    const checkNotificationPreferences = () => {
      const savedPreferences = localStorage.getItem('notificationPreferences');
      if (!savedPreferences) {
        return false;
      }

      try {
        const preferences = JSON.parse(savedPreferences);
        const enabledTypes = preferences.filter((pref: { enabled: boolean }) => pref.enabled);
        return enabledTypes.length > 0;
      } catch (error) {
        // JSON parsing errors for corrupted localStorage data - report but don't break the app
        reportError(error instanceof Error ? error : new Error('Failed to parse notification preferences'), {
          component: 'NotificationBadge',
          savedPreferences,
        });
        return false;
      }
    };

    const fetchTokenIfNeeded = () => {
      if (checkNotificationPreferences()) {
        PushNotificationService.getToken()
          .then((token) => setFcmToken(token))
          .catch(() => setFcmToken(null));
      } else {
        setFcmToken(null);
      }
    };

    // Initial check
    fetchTokenIfNeeded();

    // Listen for changes in notification preferences
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'notificationPreferences') {
        fetchTokenIfNeeded();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const { data: unreadData } = useQuery(
    ['unread-notifications-count', fcmToken],
    async () => {
      if (!fcmToken) {
        return null;
      }

      const result = await getUnreadNotificationsCount(fcmToken);
      return result;
    },
    {
      enabled: !!fcmToken,
      refetchInterval: 60000, // Refetch every 60 seconds
      refetchOnWindowFocus: true,
      staleTime: 0,
      cacheTime: 0,
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
  );

  const unreadCount = unreadData?.unreadCount || 0;

  const badgeStyles = css`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  `;

  const countBadgeStyles = css`
    position: absolute;
    top: -8px;
    right: -8px;
    background-color: ${colors.berry};
    color: ${colors.sugar};
    border-radius: 50%;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    ${fontStyles.cat};
    font-size: 11px;
    font-weight: 700;
    z-index: 10;
    min-width: 20px;
    padding: 0 2px;

    /* Hide if count is 0 */
    opacity: ${unreadCount > 0 ? 1 : 0};
    transform: scale(${unreadCount > 0 ? 1 : 0});
    transition: all 0.2s ease-in-out;
  `;

  return (
    <div css={badgeStyles}>
      {children}
      <div css={countBadgeStyles}>{unreadCount > 99 ? '99+' : unreadCount}</div>
    </div>
  );
};

export default NotificationBadge;
